import DetailRow from '@ui/elements/DetailRow'
import Price from '@/price/Price'
import useCartState from '@order/hooks/useCartState'
import useDeal from '@order/hooks/useDeal'
import { useCustomDealSettings } from '@order/hooks/useDealSettings'
import { PaymentStatusAmounts } from '@order/hooks/usePaymentStatusAmounts'
import { PaymentDetails } from '@order/hooks/usePayments'
import Typography from '@ui/typography'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

export default function InstallmentDetails({
  amounts,
  payment,
  override,
}: {
  amounts: PaymentStatusAmounts
  payment: PaymentDetails
  override?: {
    installments: number
    monthlyAmount: number
    totalAmount: number
  }
}) {
  const { t } = useTranslation(['payment', 'screens', 'ui'])
  const text = 'payment:status.details'

  // We need to get the deal from the first order as for now monthly payment only supports one order
  const orderId = payment.orderIds[0]
  const orders = useCartState((s) => s.orders)
  const order = useMemo(
    () => orders.find((o) => o.id === orderId),
    [orders, orderId]
  )
  const deal = useDeal(order?.deals.base?.dealId)

  const settings = useCustomDealSettings(deal) // NOTE: this relates to deal, not payment method
  const installments = override?.installments ?? settings.installments ?? 1
  const monthlyAmount = override?.monthlyAmount ?? amounts.toPay
  const totalAmount = override?.totalAmount ?? amounts.total

  return (
    <View className="flex flex-col gap-0 overflow-hidden rounded-xl border border-black/10">
      <DetailRow
        first
        size="sm"
        icon="clock"
        title={t(`${text}.duration.label`)}
      >
        <Text>{t(`${text}.duration.value`, { count: installments })}</Text>
      </DetailRow>
      <DetailRow
        size="sm"
        icon="calendar-event"
        title={t(`${text}.monthly.label`)}
      >
        <Text>
          <Price value={monthlyAmount} />
        </Text>
      </DetailRow>
      <DetailRow
        size="sm"
        stronger
        icon="shopping-bag-filled"
        title={t(`${text}.total.label`)}
      >
        <Text>
          <Price value={totalAmount} />
        </Text>
      </DetailRow>
    </View>
  )
}

const Text = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="SMB" className="color-text">
    {children}
  </Typography>
)
